import * as React from "react";
import * as Toast from "@radix-ui/react-toast";
import { XIcon } from '@phosphor-icons/react';
import { CSSProperties, useEffect, useState, useContext, useRef } from "react";
import { StatsContext, FeaturesContext } from '@/app/contexts';
export enum ToastLevel {
  INFO = 'info',
  ERROR = 'error',
  SUCCESS = 'success',
  WARNING = 'warning',
}
export const ToastProvider = Toast.Provider;
export const ToastViewport = Toast.Viewport;
const toastColors = (level: ToastLevel) => {
  switch (level) {
    case ToastLevel.ERROR:
      return {
        titleBg: 'bg-red-700',
        titleText: 'text-white',
        descriptionBg: 'bg-white',
        descriptionText: 'text-black',
        actionBg: 'bg-red-700',
        actionText: 'text-white'
      };
    case ToastLevel.SUCCESS:
      return {
        titleBg: 'bg-green-700',
        titleText: 'text-white',
        descriptionBg: 'bg-white',
        descriptionText: 'text-black',
        actionBg: 'bg-green-700',
        actionText: 'text-white'
      };
    case ToastLevel.WARNING:
      return {
        titleBg: 'bg-yellow-500',
        titleText: 'text-white',
        descriptionBg: 'bg-white',
        descriptionText: 'text-black',
        actionBg: 'bg-yellow-500',
        actionText: 'text-white'
      };
    case ToastLevel.INFO:
    default:
      return {
        titleBg: 'bg-blue-500',
        titleText: 'text-white',
        descriptionBg: 'bg-white',
        descriptionText: 'text-black',
        actionBg: 'bg-blue-500',
        actionText: 'text-white'
      };
  }
};
const getToastLocation = (newLocation: string, shouldClose: boolean): CSSProperties => {
  // in pixels
  const toastHeight = 180;
  const toastWidth = 380;
  const crustOnly = 10;
  const toastHorizontalMoveToClose = toastWidth;
  const toastVerticalMoveToClose = toastHeight;
  const centerHorizontalPx = `calc(50% - ${toastWidth / 2}px)`;
  if (newLocation === 'top' || newLocation === 'top-left' || newLocation === 'top-right') {
    if (newLocation === 'top-left') {
      return shouldClose ? {
        width: `${toastWidth}px`,
        height: `${toastHeight}px`,
        top: '90px',
        left: `-${toastHorizontalMoveToClose}px`
      } : {
        width: `${toastWidth}px`,
        height: `${toastHeight}px`,
        top: '90px',
        left: '20px'
      };
    } else if (newLocation === 'top-right') {
      return shouldClose ? {
        width: `${toastWidth}px`,
        height: `${toastHeight}px`,
        top: '90px',
        right: `-${toastHorizontalMoveToClose}px`
      } : {
        width: `${toastWidth}px`,
        height: `${toastHeight}px`,
        top: '90px',
        right: '10px'
      };
    } else {
      return shouldClose ? {
        width: `${toastWidth}px`,
        height: `${toastHeight}px`,
        top: `-${toastVerticalMoveToClose - crustOnly}px`,
        left: `${centerHorizontalPx}`
      } : {
        width: `${toastWidth}px`,
        height: `${toastHeight}px`,
        top: '90px',
        left: `${centerHorizontalPx}`
      };
    }
  } else if (newLocation === 'bottom' || newLocation === 'bottom-left' || newLocation === 'bottom-right') {
    if (newLocation === 'bottom-left') {
      return shouldClose ? {
        width: `${toastWidth}px`,
        height: `${toastHeight}px`,
        bottom: '10px',
        left: `-${toastHorizontalMoveToClose}px`
      } : {
        width: `${toastWidth}px`,
        height: `${toastHeight}px`,
        bottom: '10px',
        left: '20px'
      };
    } else if (newLocation === 'bottom-right') {
      return shouldClose ? {
        width: `${toastWidth}px`,
        height: `${toastHeight}px`,
        bottom: '10px',
        right: `-${toastHorizontalMoveToClose}px`
      } : {
        width: `${toastWidth}px`,
        height: `${toastHeight}px`,
        bottom: '10px',
        right: '80px'
      };
    } else {
      return shouldClose ? {
        width: `${toastWidth}px`,
        height: `${toastHeight}px`,
        bottom: `-${toastVerticalMoveToClose}px`,
        left: `${centerHorizontalPx}`
      } : {
        width: `${toastWidth}px`,
        height: `${toastHeight}px`,
        bottom: '10px',
        left: `${centerHorizontalPx}`
      };
    }
  }
  return {};
};
export interface TpToastProps {
  setOpen: (open: boolean) => void;
  open?: boolean;
  title?: string;
  description?: string;
  child?: React.ReactNode;
  actionButtonText?: string;
  actionButtonOnClick?: () => void;
  level: ToastLevel;
  duration: number;
  location: 'top' | 'top-left' | 'top-right' | 'bottom' | 'bottom-left' | 'bottom-right';
  time: number;
}
;
const TpToast = ({
  setOpen,
  open,
  title,
  description,
  child,
  actionButtonText,
  actionButtonOnClick,
  level,
  duration = 3600000,
  // 1 hour
  location,
  time
}: TpToastProps) => {
  const [shouldClose, setShouldClose] = useState(false);
  const [locationSpecs, setLocationSpecs] = useState<CSSProperties>();
  const timer = useRef<NodeJS.Timeout | null>(null);
  const timeRef = useRef<number | null>(null);
  const setClose = () => {
    setLocationSpecs(getToastLocation(location, true));
  };
  useEffect(() => {
    if (open) {
      timeRef.current = time;
      setShouldClose(false);
      setOpen(true);
      setLocationSpecs(getToastLocation(location, false));
      if (!shouldClose) {
        if (timer.current) {
          clearTimeout(timer.current);
        }
        if (duration && !Number.isNaN(duration) && duration > 0) {
          timer.current = setTimeout(() => {
            if (timeRef.current === time) {
              setLocationSpecs(getToastLocation(location, true));
            }
          }, duration);
        }
      }
    }
  }, [duration, location, open, shouldClose, time]);
  const toastClass = `box-border list-none absolute rounded-md z-[9999] duration-500 ${toastColors(level).descriptionBg}`;
  const statsContext = useContext(StatsContext);
  const {
    featureFlags
  } = useContext(FeaturesContext) || {};
  return <>
      <Toast.Root className={toastClass} style={locationSpecs} open={open} duration={!Number.isNaN(duration) ? duration : 3000} data-sentry-element="unknown" data-sentry-source-file="toast.tsx">
        <Toast.Title className={`${toastColors(level).titleBg} rounded-t-md p-2 font-semibold h-[24px]`} data-sentry-element="unknown" data-sentry-source-file="toast.tsx">
          <div className="flex flex-row justify-between items-center">
            <div className={`${toastColors(level).titleText}`}>{title}</div>
            <XIcon size={24} color={`${toastColors(level).titleText}`} onClick={setClose} className="cursor-pointer m-0 p-0 fill-white" data-sentry-element="XIcon" data-sentry-source-file="toast.tsx" />
          </div>
        </Toast.Title>
        <Toast.Description className={`${toastColors(level).descriptionBg} text-md p-2`} data-sentry-element="unknown" data-sentry-source-file="toast.tsx">
          <div className={`${toastColors(level).descriptionBg} ${toastColors(level).descriptionText}`}>{description}</div>
        </Toast.Description>
        {child}
        {actionButtonText && actionButtonOnClick && <button id="ToastActionButton" className={`cursor-pointer ${toastColors(level).actionText} ${toastColors(level).actionBg} small right-0 bottom-0 absolute m-2 rounded-sm p-2`} onClick={event => {
        actionButtonOnClick();
        setClose();
        statsContext.recordStat('ToastActionButtonClicked', event, {
          userInfo: {
            userId: statsContext?.userInfo?.userId || '',
            sessionId: statsContext?.userInfo?.sessionId || '',
            caseId: statsContext?.userInfo?.caseId || '',
            query: actionButtonText || ''
          }
        }, !!featureFlags?.useIntercom);
      }}>
            {actionButtonText}
          </button>}
      </Toast.Root>
    </>;
};
export default TpToast;